import * as React from "react";

import {Form, FormListener} from "../form/Form";
import {FormInput} from "../form/FormInput";
import {Validators} from "../form/Validators";
import {Submit} from "../form/Submit";
import {FormSubmitError, FormSubmitSuccess} from "../form/FormFeedback";
import {Collapsible} from "../Collapsible";
import {Currency, ManualPayoutRequest, Settings, TransferRequest} from "../../http/protocol";
import {getContainer} from "../../ioc/IOCSetup";
import {SelectInput} from "../form/SelectInput";
import {AffiliatesService} from "../../services/AffiliatesService";
import { CurrencyEuroValue } from "../account/CurrencyEuroValue";
import { LargeAmountConfirmationDialog } from "../dialog/LargeAmountConfirmationDialog";
import { LargeAmountConfirmationHandler } from "../util/largeAmountConfirmation";
import { SupervisorValidationModal } from "../dialog/SupervisorValidationModal";
import { HttpError } from "../../http/Types";

interface Props {
    transfer : (manualPayoutRequest : ManualPayoutRequest) => Promise<void>;
    affiliateId?:number,
    payoutCurrency: string,
    currencyEuroValueEnabled?: boolean
}
interface State {
    transactionForm? : ManualPayoutRequest;
    validationMap? : any;
    affiliateId?:number;
    showConfirmationDialog: boolean;
    euroAmount?: number;
    largeAmountThreshold?: number;
    userCancelled?: boolean;
    supervisorValidationMessage?: string;
}

export class AffiliateTransactionPanel extends React.Component<Props, State> implements FormListener<ManualPayoutRequest> {
    affiliatesService: AffiliatesService;
    private confirmationHandler: LargeAmountConfirmationHandler;

    constructor(props : Props) {
        super(props);
        this.affiliatesService = getContainer().getAffiliatesService();
        this.state = {
            transactionForm: {},
            validationMap: {},
            showConfirmationDialog: false,
            userCancelled: false
        };
        this.confirmationHandler = new LargeAmountConfirmationHandler();
        console.log("this.props.affiliateId " + this.props.affiliateId)
    }

    componentDidMount() {
        this.confirmationHandler.loadSecuritySettings();
    }

    componentWillUnmount() {
        this.confirmationHandler.reset();
    }

    async checkThresholdAndSubmit(): Promise<any> {
        const { transactionForm } = this.state;

        this.setState({ userCancelled: false });

        if (!transactionForm || !transactionForm.amount) {
            return;
        }

        const currency = this.props.payoutCurrency;

        try {
            const result = await this.confirmationHandler.checkThreshold(
                transactionForm.amount,
                currency
            );

            if (result.requiresConfirmation) {
                this.setState({
                    euroAmount: result.euroAmount,
                    showConfirmationDialog: true,
                    largeAmountThreshold: this.confirmationHandler.getThreshold()
                });

                try {
                    await this.confirmationHandler.waitForConfirmation();
                } catch (error: any) {
                    if (error?.message === "USER_CANCELLED") {
                        this.setState({ userCancelled: true });
                        throw error;
                    }
                    throw error;
                }
            }

            return this.performTransfer();
        } catch (error: any) {
            if (error?.message === "USER_CANCELLED") {
                this.setState({ userCancelled: true });
                throw error;
            }
            console.error("Error checking threshold:", error);
            this.confirmationHandler.resetSubmissionFlag();
            return this.performTransfer();
        }
    }

    performTransfer(): Promise<any> {
        if (this.state.transactionForm) {
            const request = { ...this.state.transactionForm, affiliateId: this.props.affiliateId, currency: this.props.payoutCurrency };
            return this.props.transfer(request).then(() => {
                this.setState({
                    transactionForm: Object.assign({}, this.state.transactionForm, {
                        amount: ""
                    })
                });
            }).catch((error: HttpError) => {
                if (error.isSupervisorRequired) {
                    // Supervisor approval is pending (this payout or an earlier one), so it is not an error; userCancelled hides the form's success/error modals.
                    this.setState({ supervisorValidationMessage: error.message, showConfirmationDialog: false, userCancelled: true });
                    return;
                }
                throw error.message;
            });
        } else {
            throw "Unable to perform action";
        }
    }

    onSubmit(): Promise<any> {
        return this.checkThresholdAndSubmit();
    }

    handleConfirmLargeAmount = () => {
        this.setState({
            showConfirmationDialog: false,
            euroAmount: undefined,
            userCancelled: false
        });
        this.confirmationHandler.confirmLargeAmount();
    }

    handleCancelLargeAmount = () => {
        this.setState({
            showConfirmationDialog: false,
            euroAmount: undefined
        });
        this.confirmationHandler.cancelLargeAmount();
    }

    formDidUpdate(formModel: TransferRequest, validationMap?: any) {
        this.setState({transactionForm : formModel, validationMap : validationMap});
    }


    getForm() {
        return (
            <Form className="manual-adjustment-form container-fluid" formListener={this} validationMap={this.state.validationMap} modelObject={this.state.transactionForm}>

                <div className="row">
                    <div className="col-sm-6">
                        <label>Amount:</label>
                        <FormInput className="form-control"  model="amount" type="float" validators={[Validators.required(), Validators.number()]}/>
                        {this.props.currencyEuroValueEnabled && (
                            <CurrencyEuroValue
                                sourceCurrency={this.props.payoutCurrency}
                                sourceAmount={this.state.transactionForm?.amount}
                            />
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <label>Currency:</label>
                        <input className="form-control"  value={this.props.payoutCurrency} disabled={true}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-8">
                        <label>affiliateId:</label>
                        <input className="form-control"  value={this.props.affiliateId} disabled={true}/>

                    </div>
                    <div className="col-sm-4">
                        <label>&nbsp;</label>
                        {!this.state.userCancelled && <FormSubmitError />}
                        {!this.state.userCancelled && <FormSubmitSuccess text={"Transfer successful!"}/>}
                        <Submit className="btn btn-primary btn-block">Transfer</Submit>
                    </div>
                </div>
            </Form>
        );
    }

   render() {
        return (
            <>
                <Collapsible title="Register Manual Affiliate Payout" collapsed={false}>
                    {this.getForm()}
                </Collapsible>

                <SupervisorValidationModal
                    show={!!this.state.supervisorValidationMessage}
                    message={this.state.supervisorValidationMessage || ""}
                    onClose={() => this.setState({ supervisorValidationMessage: undefined })}
                />

                {this.state.showConfirmationDialog && this.state.transactionForm?.amount && this.props.payoutCurrency && (
                    <LargeAmountConfirmationDialog
                        show={this.state.showConfirmationDialog}
                        originalAmount={this.state.transactionForm.amount as number}
                        originalCurrency={this.props.payoutCurrency}
                        euroAmount={this.state.euroAmount || 0}
                        onConfirm={this.handleConfirmLargeAmount}
                        onCancel={this.handleCancelLargeAmount}
                    />
                )}
            </>
        );
    }
}